import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Award, Users, FileCheck, Activity, AlertTriangle,
  Loader2, Printer, ExternalLink, CheckCircle2, Clock, Rocket,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Award, label: 'Compliance', active: true },
  { icon: Activity, label: 'Activity Ledger', href: '/admin/activity-ledger' },
  { icon: Shield, label: 'Ops', href: '/admin/ops' },
];

export default function AdminCompliance() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(
        `${API}/admin/compliance/attestation`,
        getAuthHeader(),
      );
      setData(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load attestation');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const printPage = () => window.print();

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Compliance Attestation"
      subtitle="Single-glance HIPAA / SOC 2 posture. Safe to print, safe to share with auditors. No PHI — aggregate posture metrics only."
      testId="admin-compliance-page"
      rightSlot={
        <Button
          onClick={printPage}
          className="cn-btn cn-btn-primary !py-1.5 !px-3 text-xs"
          data-testid="compliance-print-btn"
        >
          <Printer className="w-3.5 h-3.5" /> Print / Save PDF
        </Button>
      }
    >
      {loading || !data ? (
        <div className="cn-card p-10 text-center" data-testid="compliance-loading">
          <Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" />
        </div>
      ) : (
        <div className="space-y-6 print:space-y-4">
          {/* Header meta */}
          <div className="cn-card p-4 flex flex-wrap items-center gap-4 text-xs text-cn-text-muted" data-testid="compliance-meta">
            <span>Generated: {new Date(data.generated_at).toLocaleString()}</span>
            <span>·</span>
            <span>{data.generator}</span>
          </div>

          {/* Tenancy coverage HERO */}
          <TenancyHero coverage={data.tenancy_coverage} />

          {/* Three-up: security posture, MFA, HIPAA exports */}
          <div className="grid md:grid-cols-3 gap-4">
            <SecurityPostureCard posture={data.security_posture} />
            <MfaCard mfa={data.mfa_enrollment} />
            <ExportsCard exports={data.hipaa_export_activity} />
          </div>

          {/* Audit activity + breach snapshot */}
          <div className="grid md:grid-cols-2 gap-4">
            <AuditActivityCard audit={data.audit_activity_30d} />
            <BreachSnapshotCard breach={data.breach_snapshot} />
          </div>

          {/* HIPAA Readiness Iter 139 — Deploy Preflight tile */}
          <DeployPreflightCard />

          {/* HIPAA sections referenced */}
          <div className="cn-card p-5" data-testid="compliance-hipaa-sections">
            <div className="flex items-center gap-2 mb-3">
              <FileCheck className="w-4 h-4 text-cn-primary" />
              <h3 className="cn-display text-base text-cn-text">HIPAA sections this posture supports</h3>
            </div>
            <ul className="text-xs text-cn-text-muted space-y-1">
              {(data.hipaa_sections_referenced || []).map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Documentation pointers */}
          <div className="cn-card p-5" data-testid="compliance-docs">
            <div className="flex items-center gap-2 mb-3">
              <ExternalLink className="w-4 h-4 text-cn-primary" />
              <h3 className="cn-display text-base text-cn-text">Supporting documentation</h3>
            </div>
            <ul className="text-xs space-y-1">
              {Object.entries(data.documentation || {}).map(([k, v]) => (
                <li key={k}>
                  <span className="text-cn-text-muted">{k.replace(/_/g, ' ')}: </span>
                  {v.startsWith('/api/') || v.startsWith('/admin/') ? (
                    <a href={v} className="text-cn-primary hover:underline">{v}</a>
                  ) : (
                    <span className="font-mono">{v}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Print stylesheet — hide sidebar/topbar and expand cards */}
          <style>{`
            @media print {
              body { background: white; }
              [data-testid="dashboard-sidebar"], nav[role="navigation"] { display: none !important; }
              .cn-card { break-inside: avoid; }
            }
          `}</style>
        </div>
      )}
    </DashboardShell>
  );
}

// ── sub-cards ────────────────────────────────────────────────────────

function TenancyHero({ coverage }) {
  const pct = coverage?.coverage_pct ?? 0;
  const isPerfect = coverage?.baseline_count === 0;
  return (
    <div
      className={`cn-card p-6 border-2 ${isPerfect ? 'border-emerald-300 bg-emerald-50/50' : 'border-amber-300 bg-amber-50/40'}`}
      data-testid="compliance-tenancy-hero"
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isPerfect ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {isPerfect ? <Award className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-cn-text-muted">Tenancy Coverage · T9 Driver</div>
          <div className="cn-display text-3xl text-cn-text mt-1" data-testid="compliance-tenancy-pct">
            {pct}%
          </div>
          <div className="text-xs text-cn-text-muted mt-2">
            {isPerfect ? (
              <>Every read of a PHI-holding collection in the routes tree has an explicit tenancy assertion.
              Zero baseline violations. The pytest guardrail fails CI on the first new unscoped read.</>
            ) : (
              <>{coverage.baseline_count} pre-existing violation(s) still in the baseline
              (down from a high-water mark of {coverage.high_water_mark}).</>
            )}
          </div>
          <div className="flex items-center gap-6 mt-4 text-xs">
            <MetricPill label="Baseline" value={coverage?.baseline_count ?? '—'} />
            <MetricPill label="HWM" value={coverage?.high_water_mark ?? '—'} />
            <MetricPill label="Closed" value={coverage?.closed_since_origin ?? '—'} />
            <MetricPill label="History pts" value={coverage?.history_points ?? '—'} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SecurityPostureCard({ posture }) {
  const flags = [
    { label: 'CSP enforced', on: posture?.csp_enforce, muted: 'Report-only in preview.' },
    { label: 'Trusted proxies set', on: posture?.trusted_proxies_configured },
    { label: 'HIPAA prod mode', on: posture?.hipaa_prod_mode },
    { label: 'LLM BAA signed', on: posture?.llm_baa_signed },
    { label: 'SSRF dev override off', on: !posture?.ssrf_dev_override, danger: posture?.ssrf_dev_override },
  ];
  return (
    <div className="cn-card p-5" data-testid="compliance-posture-card">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-cn-primary" />
        <h3 className="cn-display text-base text-cn-text">Security posture</h3>
      </div>
      <ul className="space-y-2 text-xs">
        {flags.map((f) => (
          <li key={f.label} className="flex items-center gap-2" data-testid={`posture-${f.label.toLowerCase().replace(/\s/g, '-')}`}>
            <span className={`w-2 h-2 rounded-full ${f.on ? 'bg-emerald-500' : (f.danger ? 'bg-red-500' : 'bg-amber-400')}`} />
            <span className="text-cn-text">{f.label}</span>
            <span className="ml-auto text-cn-text-muted">{f.on ? 'on' : 'off'}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 pt-3 border-t border-cn-border/40 text-[11px] text-cn-text-muted">
        LLM: <span className="font-mono">{posture?.llm_provider}</span>
        {' · '}
        Max upload: <span className="font-mono">{posture?.max_upload_bytes}</span>
      </div>
    </div>
  );
}

function MfaCard({ mfa }) {
  const admin = mfa?.admin || {};
  const clin = mfa?.clinician || {};
  return (
    <div className="cn-card p-5" data-testid="compliance-mfa-card">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-cn-primary" />
        <h3 className="cn-display text-base text-cn-text">MFA enrollment</h3>
      </div>
      <MfaRow role="Admin" data={admin} required />
      <MfaRow role="Clinician" data={clin} />
      <div className="mt-3 pt-3 border-t border-cn-border/40 text-[11px] text-cn-text-muted">
        Iter 133 hard-enforced MFA for admins. Clinicians on 7-day grace.
      </div>
    </div>
  );
}

function MfaRow({ role, data, required }) {
  const pct = data?.coverage_pct ?? 0;
  const isFull = pct >= 100;
  return (
    <div className="mt-2" data-testid={`mfa-row-${role.toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-cn-text">
          {role} {required ? <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded ml-1">required</span> : null}
        </span>
        <span className={`font-semibold ${isFull ? 'text-emerald-700' : 'text-amber-700'}`}>
          {data?.enrolled ?? 0} / {data?.total ?? 0} · {pct}%
        </span>
      </div>
      <div className="mt-1 h-1.5 bg-cn-bg/40 rounded-full overflow-hidden">
        <div
          className={`h-full ${isFull ? 'bg-emerald-500' : 'bg-amber-500'}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

function ExportsCard({ exports }) {
  return (
    <div className="cn-card p-5" data-testid="compliance-exports-card">
      <div className="flex items-center gap-2 mb-3">
        <FileCheck className="w-4 h-4 text-cn-primary" />
        <h3 className="cn-display text-base text-cn-text">HIPAA §164.524 exports</h3>
      </div>
      <div className="space-y-2 text-xs">
        <Row label="Delivered (30d)" value={exports?.delivered_30d ?? 0} />
        <Row label="In flight" value={exports?.in_flight ?? 0} highlight={exports?.in_flight > 0} />
        <Row label="Admin-initiated (30d)" value={exports?.admin_initiated_30d ?? 0} />
        <Row label="Failed (30d)" value={exports?.failed_30d ?? 0} danger={exports?.failed_30d > 0} />
      </div>
    </div>
  );
}

function AuditActivityCard({ audit }) {
  return (
    <div className="cn-card p-5" data-testid="compliance-audit-card">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-cn-primary" />
        <h3 className="cn-display text-base text-cn-text">Audit activity (30d)</h3>
      </div>
      <div className="text-xs">
        <div className="text-cn-text-muted mb-2">
          Total events: <span className="font-semibold text-cn-text">{(audit?.total_events || 0).toLocaleString()}</span>
        </div>
        <div className="mb-1 font-semibold text-cn-text">By role:</div>
        <ul className="space-y-0.5">
          {(audit?.by_role || []).map((r) => (
            <li key={r.role} className="flex justify-between">
              <span>{r.role}</span>
              <span className="font-mono">{r.count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BreachSnapshotCard({ breach }) {
  const admin_writes = breach?.admin_writes || 0;
  const deletes = breach?.delete_events || 0;
  return (
    <div className="cn-card p-5" data-testid="compliance-breach-card">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-cn-primary" />
        <h3 className="cn-display text-base text-cn-text">Breach snapshot (24h)</h3>
      </div>
      <div className="text-xs space-y-2">
        <Row label="Total events" value={(breach?.total_events || 0).toLocaleString()} />
        <Row label="Admin writes" value={admin_writes} highlight={admin_writes > 50} />
        <Row label="Delete events" value={deletes} danger={deletes > 5} />
      </div>
      <div className="mt-3 pt-3 border-t border-cn-border/40 text-[11px] text-cn-text-muted">
        <Clock className="inline w-3 h-3 mr-1" />
        Detailed heuristics at{' '}
        <a href={breach?.detailed_signals_href} className="text-cn-primary hover:underline">activity ledger</a>.
      </div>
    </div>
  );
}

function MetricPill({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-cn-text-muted">{label}</span>
      <span className="cn-display text-lg text-cn-text">{value}</span>
    </div>
  );
}

function Row({ label, value, highlight, danger }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-cn-text-muted">{label}</span>
      <span className={`font-mono ${danger ? 'text-red-600 font-semibold' : (highlight ? 'text-amber-700 font-semibold' : 'text-cn-text')}`}>
        {value}
      </span>
    </div>
  );
}

// ── Deploy Preflight ─────────────────────────────────────────────────
// HIPAA Readiness Iter 139 — pulls /api/admin/deploy/preflight and
// shows a red/amber/green scoreboard so an admin can confirm every
// production hard-gate is set before a redeploy.

function DeployPreflightCard() {
  const { getAuthHeader } = useAuth();
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/deploy/preflight`,
          getAuthHeader(),
        );
        if (!cancelled) setPreflight(data);
      } catch (e) {
        if (!cancelled) setPreflight({ error: e.response?.data?.detail || 'load failed' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  const sev = preflight?.severity || 'amber';
  const heroBg = sev === 'green' ? 'border-emerald-300 bg-emerald-50/50'
    : sev === 'red' ? 'border-red-300 bg-red-50/50'
    : 'border-amber-300 bg-amber-50/40';
  const heroIcon = sev === 'green' ? 'bg-emerald-100 text-emerald-700'
    : sev === 'red' ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-700';

  return (
    <div className={`cn-card p-6 border-2 ${heroBg}`} data-testid="compliance-preflight-card">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${heroIcon}`}>
          <Rocket className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-cn-text-muted">
            Production Deploy Preflight
          </div>
          <div className="cn-display text-xl text-cn-text mt-1">
            {loading ? 'Checking…' : (
              preflight?.error ? 'Could not load'
                : sev === 'green' ? 'Ready to ship 🚀'
                : sev === 'red' ? 'Blocked — resolve the red checks below'
                : 'Advisory warnings only'
            )}
          </div>
          {preflight?.summary && (
            <div className="text-xs text-cn-text-muted mt-2 flex gap-4">
              <span data-testid="preflight-green-count">✅ {preflight.summary.green} green</span>
              <span data-testid="preflight-amber-count">⚠️  {preflight.summary.amber} amber</span>
              <span data-testid="preflight-red-count">❌ {preflight.summary.red} red</span>
              <span className="text-cn-text-muted/70">of {preflight.summary.total} checks</span>
            </div>
          )}
        </div>
      </div>

      {preflight?.checks && (
        <div className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-2 text-xs" data-testid="preflight-checks-grid">
          {preflight.checks.map((c) => (
            <div
              key={c.id}
              className="flex items-start gap-2 py-1.5 border-b border-cn-border/30 last:border-none"
              data-testid={`preflight-check-${c.id}`}
            >
              <span
                className={
                  c.status === 'green' ? 'text-emerald-600'
                    : c.status === 'red' ? 'text-red-600'
                    : 'text-amber-600'
                }
              >
                {c.status === 'green' ? '✅' : c.status === 'red' ? '❌' : '⚠️'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-cn-text font-medium">{c.name}</div>
                <div className="text-cn-text-muted font-mono text-[11px] break-words">
                  {c.details}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preflight?.next_steps && preflight.next_steps.length > 0 && (
        <div className="mt-4 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="font-semibold mb-1">Fix before redeploy:</div>
          <ul className="list-disc list-inside space-y-0.5" data-testid="preflight-next-steps">
            {preflight.next_steps.map((step) => (
              <li key={step} className="font-mono">{step}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-cn-border/40 text-[11px] text-cn-text-muted">
        CLI equivalent: <code className="font-mono">python3 -m scripts.deploy_preflight</code>
      </div>
    </div>
  );
}
